import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Typography } from '@mui/material';
import FlagRounded from '@mui/icons-material/FlagRounded';
import TSButton from 'components/common/Button/Button';
import MLModal from 'components/common/Modal/Modal';
import Notice from 'components/common/Notice/Notice';
import StatusBadge from 'components/common/StatusBadge/StatusBadge';
import TSCard from 'components/common/TSCard/TSCard';
import PlanFacts from 'components/TrailSafe/PlanFacts/PlanFacts';

import { ROUTES } from 'constants/RoutePaths';
import { buildRoutePath } from 'utils/utility';
import { trailToday } from 'utils/trailTime';
import { setQRCodeId } from 'app/state';
import { userToken } from '_actions/user_actions';

import { useGetTrailListByQRCodeQuery } from 'app/api/trailApiSlice';
import { useGetPlansForQrCodeQuery } from 'app/api/planApiSlice';
import StartPlanCheck from 'pages/Protected/Plans/StartPlanCheck';

// A scanned QR Code. A signed-in walker with a Plan for today on one of its
// Trails is offered "Start your Plan", prefilled. Otherwise they pick a
// Trail (or go straight to it when the QR Code lists one) and start now,
// which makes a Plan for now and starts it (ADR-0014). Either way the QR
// Code is recorded on the check-in.
const TrailsByQRCode = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isAuth, setIsAuth] = useState(null);
  const [planIdToStart, setPlanIdToStart] = useState(null);
  const closeStartCheck = (open) => !open && setPlanIdToStart(null);

  // The QR Code's permanent code from the sign's URL, e.g. CMT-DL7R.
  const { qrCode } = useParams();

  useEffect(() => {
    dispatch(setQRCodeId(qrCode));
  }, [dispatch, qrCode]);

  useEffect(() => {
    userToken()
      .then((token) => setIsAuth(!!token))
      .catch(() => setIsAuth(false));
  }, []);

  const {
    data: trail,
    isLoading: isTrailLoading,
    error,
  } = useGetTrailListByQRCodeQuery(qrCode);
  // A failed lookup just means no Plan is offered; starting now still works.
  const { data: todaysPlans, isLoading: isPlansLoading } =
    useGetPlansForQrCodeQuery(qrCode, { skip: !isAuth });

  const isCheckingPlans = isAuth === null || (isAuth && isPlansLoading);
  const offeredPlans = (isAuth && todaysPlans) || [];

  useEffect(() => {
    if (!isCheckingPlans && offeredPlans.length === 0 && trail?.length === 1) {
      navigate(
        `/${buildRoutePath(ROUTES.TRAIL_DETAIL, {
          trailId: trail[0].trailid,
        })}`
      );
    }
  }, [navigate, trail, isCheckingPlans, offeredPlans.length]);

  if (isTrailLoading || isCheckingPlans) {
    return <div className="ts-page">Loading...</div>;
  }

  if (error?.status === 404) {
    return (
      <div className="ts-page flex flex-col gap-4">
        <Typography variant="h2">QR Code not found</Typography>
        <Notice tone="warning" title={`We couldn't find the code ${qrCode}`}>
          Check the code on the sign, or browse all trails instead.
        </Notice>
        <TSButton
          btnText="Browse all trails"
          onClickFunc={() => navigate(`/${ROUTES.TRAIL_HOME}`)}
        />
      </div>
    );
  }

  if (error) {
    return <div className="ts-page">Error loading trails.</div>;
  }

  const today = trailToday();
  const openTrail = (trailId) =>
    navigate(`/${buildRoutePath(ROUTES.TRAIL_DETAIL, { trailId })}`);

  return (
    <>
      {/* With a Plan to offer, on a laptop it sits beside "start now". */}
      <div
        className={`ts-page flex flex-col gap-8 ${
          offeredPlans.length > 0
            ? 'ts-page--wide lg:grid lg:grid-cols-2 lg:items-start'
            : ''
        }`}
      >
        {offeredPlans.length > 0 && (
          <section className="flex flex-col gap-4">
            <Typography variant="h2">Start your Plan</Typography>
            <ul className="flex flex-col gap-4">
              {offeredPlans.map((plan) => (
                <TSCard
                  as="li"
                  key={plan.plan_id}
                  tone="brand"
                  title={`${plan.trail_name}, ${plan.park_name}`}
                  action={<StatusBadge status="today" />}
                >
                  <PlanFacts plan={plan} today={today} />
                  <div className="flex flex-col gap-2 mt-2">
                    {plan.needs_emergency_contact ? (
                      <TSButton
                        btnText="Open Plan"
                        onClickFunc={() =>
                          navigate(
                            `/${buildRoutePath(ROUTES.PLAN_DETAIL, {
                              planId: plan.plan_id,
                            })}`
                          )
                        }
                      />
                    ) : (
                      <TSButton
                        btnText="Start walk"
                        icon={<FlagRounded />}
                        onClickFunc={() => setPlanIdToStart(plan.plan_id)}
                      />
                    )}
                  </div>
                </TSCard>
              ))}
            </ul>
          </section>
        )}
        <section className="flex flex-col gap-4">
          {offeredPlans.length > 0 ? (
            <Typography variant="h3">Or start now instead</Typography>
          ) : (
            <Typography variant="h2">
              {trail?.length > 0
                ? 'Which trail do you plan to walk today?'
                : "This QR Code doesn't list any trails"}
            </Typography>
          )}
          {trail?.length > 0 && (
            <div className="flex flex-col gap-2">
              {trail.map((trail) => (
                <TSButton
                  key={trail.trailid}
                  btnText={trail.trailname}
                  btnVariant={
                    offeredPlans.length > 0 ? 'outlined' : 'contained'
                  }
                  onClickFunc={() => openTrail(trail.trailid)}
                />
              ))}
            </div>
          )}
        </section>
      </div>
      <MLModal open={planIdToStart !== null} setOpenModal={closeStartCheck}>
        {planIdToStart && (
          <StartPlanCheck
            planId={planIdToStart}
            qrCode={qrCode}
            toggleModal={closeStartCheck}
          />
        )}
      </MLModal>
    </>
  );
};

export default TrailsByQRCode;
